import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { apiClient } from '@/services/api'
import { useSessionStore, useUIStore } from '@stores'
import { FulfillmentStatus, ProductType, ItemStatus, CancelItemsSelection, ProcessType } from '@/constants'
import { getItemImageUrl } from '@/utils'
import { EmptyState } from '@/components/ui'
import StepTitle from '@/components/StepTitle'

interface ActionItem {
  id: number
  name: string
  quantity: number
  price: number
  current_price?: number
  status?: number
  fulfillment_status?: number
  variant?: { options: Array<{ name: string; value: string }> }
  order: { id: number; currency: string }
  campaign_product: {
    id: number
    product_type: number
    process_type: number
  }
  can_cancel?: boolean
  can_return?: boolean
  can_exchange?: boolean
  [key: string]: any
}

interface SelectActionItemsProps {
  label?: string
  rebill?: boolean
  defaults?: { items: Array<{ id: number; quantity: number }> }
  session?: any
  onSelect?: (data: { items: Array<{ id: number; quantity: number }> }, mainItem: ActionItem | null) => void
  onBack?: () => void
}

interface TableDataItem {
  id: number
  image: string
  name: string
  selectedQuantity: number
  quantity: number
  price: number
  currentPrice: number
  isAddOn: boolean
  currency: string
}

const SelectActionItems = ({
  label = 'Continue',
  rebill = false,
  defaults = { items: [] },
  session: sessionProp,
  onSelect,
  onBack,
}: SelectActionItemsProps) => {
  const storeSession = useSessionStore(s => s.session) as any
  const session = sessionProp ?? storeSession
  const selectedItems = useSessionStore(s => s.selectedItems)
  const startIndex = useSessionStore(s => s.startIndex)
  const isLoading = useUIStore(s => s.isLoading)
  const setLoading = useUIStore(s => s.setLoading)

  const [items, setItems] = useState<ActionItem[]>([])
  const [linkedItems, setLinkedItems] = useState<ActionItem[]>([])
  const [rmaQuantities, setRmaQuantities] = useState<Record<number, number>>({})
  const [title, setTitle] = useState('')
  const [subtitle, setSubtitle] = useState('')
  const [isOnHold, setIsOnHold] = useState(false)
  const [showQuantityColumn, setShowQuantityColumn] = useState(false)
  const [loaded, setLoaded] = useState(false)

  const initialized = useRef(false)
  const onSelectRef = useRef(onSelect)
  useEffect(() => {
    onSelectRef.current = onSelect
  })

  const isMainItem = useCallback(
    (item: ActionItem) =>
      item.campaign_product.product_type === ProductType.Offer &&
      item.campaign_product.process_type === ProcessType.Sale,
    []
  )

  const isAddonItem = useCallback(
    (item: ActionItem) => {
      if (item.campaign_product.process_type !== ProcessType.Sale) {
        return true
      }
      return item.campaign_product.product_type === ProductType.Upsale && !!session?.campaign?.auto_cancel_upsells
    },
    [session?.campaign?.auto_cancel_upsells]
  )

  const sessionSelectedItem = session?.selected_item

  const isSessionItem = useCallback(
    (item: ActionItem) => !!(item && sessionSelectedItem && item.id === sessionSelectedItem.id),
    [sessionSelectedItem]
  )

  const isLinkedItem = useCallback((item: ActionItem) => linkedItems.some(v => v.id === item.id), [linkedItems])

  const getVariantLabel = (item: ActionItem): string => {
    if (!item.variant) {
      return ''
    }
    return item.variant.options.map(o => `${o.name}: ${o.value}`).join(', ')
  }
  const getDisplayName = useCallback((item: ActionItem): string => {
    const variantLabel = getVariantLabel(item)
    return variantLabel ? `${item.name} - ${variantLabel}` : item.name
  }, [])

  const formatCurrency = (amount: number, currency: string): string =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(amount)

  const selectionCount = useMemo(
    () => items.reduce((n, it) => n + (rmaQuantities[it.id] > 0 ? 1 : 0), 0),
    [items, rmaQuantities]
  )

  const getSelectionCount = useCallback(() => selectionCount, [selectionCount])

  // Header checkbox state is a pure function of how many rows are selected.
  const allSelected = selectionCount > 0 && selectionCount === items.length
  const indeterminate = selectionCount > 0 && selectionCount < items.length

  const areAllMainItemsSelected = useCallback(
    (qtys: Record<number, number>) => {
      let has = false
      let all = true
      items.forEach(item => {
        if (isMainItem(item)) {
          has = true
          if ((qtys[item.id] || 0) === 0) {
            all = false
          }
        }
      })
      return has && all
    },
    [items, isMainItem]
  )

  const updateSelectedItemsForCancel = useCallback(
    (qtys: Record<number, number>, selected: boolean) => {
      if (!isOnHold || !areAllMainItemsSelected(qtys)) {
        return qtys
      }
      const next = { ...qtys }
      items.forEach(item => {
        if (isAddonItem(item)) {
          if (selected) {
            next[item.id] = item.quantity
          } else if (!isSessionItem(item)) {
            next[item.id] = 0
          }
        }
      })
      return next
    },
    [isOnHold, areAllMainItemsSelected, items, isAddonItem, isSessionItem]
  )

  const updateLinkedRMAQuantities = useCallback(
    (qtys: Record<number, number>) => {
      if (!sessionSelectedItem) {
        return qtys
      }
      const selectedItemQty = qtys[sessionSelectedItem.id] || 0
      const next = { ...qtys }
      linkedItems.forEach(item => {
        const ratio = item.quantity / sessionSelectedItem.quantity
        next[item.id] = Math.floor(ratio * selectedItemQty + 0.00000001)
      })
      return next
    },
    [linkedItems, sessionSelectedItem]
  )

  const canToggleItem = useCallback(
    (item: ActionItem) => {
      if (isLinkedItem(item) || isSessionItem(item)) {
        return false
      }
      return !isOnHold || !isAddonItem(item) || !areAllMainItemsSelected(rmaQuantities)
    },
    [isLinkedItem, isSessionItem, isOnHold, isAddonItem, areAllMainItemsSelected, rmaQuantities]
  )

  const isItemChecked = useCallback(
    (item: ActionItem) => {
      if ((rmaQuantities[item.id] || 0) > 0) {
        return true
      }
      return !isLinkedItem(item) && !canToggleItem(item)
    },
    [rmaQuantities, isLinkedItem, canToggleItem]
  )

  const itemData = useMemo((): TableDataItem[] => {
    const data: TableDataItem[] = []
    const push = (it: ActionItem) => {
      data.push({
        id: it.id,
        image: getItemImageUrl(it, 'small'),
        name: getDisplayName(it),
        selectedQuantity: rmaQuantities[it.id] || 0,
        quantity: it.quantity,
        price: it.price,
        currentPrice: it.current_price ?? it.price,
        isAddOn: isAddonItem(it),
        currency: it.order.currency,
      })
    }
    for (const item of items) {
      if (!item) {
        continue
      }
      push(item)
      if (session?.selected_item && item.id === session.selected_item.id) {
        linkedItems.forEach(push)
      }
    }
    return data
    // eslint-disable-next-line react-hooks/exhaustive-deps -- React Compiler wants `session`, this rule wants `session.selected_item`; they conflict.
  }, [items, linkedItems, rmaQuantities, sessionSelectedItem, isAddonItem, getDisplayName])

  const submitItems = useCallback(
    (nextItems: ActionItem[], nextQtys: Record<number, number>, nextLinked: ActionItem[] = linkedItems) => {
      let count = 0
      nextItems.forEach(it => {
        if ((nextQtys[it.id] || 0) > 0) {
          count++
        }
      })
      if (count === 0) {
        return
      }

      const selected: Array<{ id: number; quantity: number }> = []
      let mainSelectedItem: ActionItem | null = null
      nextItems.forEach(item => {
        if ((nextQtys[item.id] || 0) > 0) {
          selected.push({ id: item.id, quantity: nextQtys[item.id] })
          if (!mainSelectedItem || isSessionItem(item)) {
            mainSelectedItem = item
          }
        }
      })
      nextLinked.forEach(item => {
        if ((nextQtys[item.id] || 0) > 0) {
          selected.push({ id: item.id, quantity: nextQtys[item.id] })
        }
      })

      if (selected.length) {
        const sessionStore = useSessionStore.getState() as any
        nextLinked.forEach(linked => {
          const isAlsoSelected = sessionStore.selectedItems?.some((s: any) => s.id === linked.id)
          if (isAlsoSelected && typeof sessionStore.removeSelectedItem === 'function') {
            sessionStore.removeSelectedItem(linked)
          }
        })
        onSelectRef.current?.({ items: selected }, mainSelectedItem)
      }
    },
    [linkedItems, isSessionItem]
  )

  const handleDone = useCallback(
    (nextQtys?: Record<number, number>) => {
      submitItems(items, nextQtys || rmaQuantities, linkedItems)
    },
    [items, linkedItems, rmaQuantities, submitItems]
  )

  const handleToggleItem = (tItem: TableDataItem, checked: boolean) => {
    const item = items.find(i => i.id === tItem.id) || linkedItems.find(i => i.id === tItem.id)
    if (!item) {
      return
    }
    setRmaQuantities(prev => {
      let next = { ...prev }
      if (checked) {
        next[item.id] = item.quantity
        next = updateSelectedItemsForCancel(next, true)
      } else if (!isSessionItem(item)) {
        next = updateSelectedItemsForCancel(next, false)
        next[item.id] = 0
      }
      if (isSessionItem(item)) {
        next = updateLinkedRMAQuantities(next)
      }
      return next
    })
  }

  const handleRowClick = (t: TableDataItem) => {
    const checked = (rmaQuantities[t.id] || 0) === 0
    handleToggleItem(t, checked)
  }

  const handleToggleAll = (checked: boolean) => {
    setRmaQuantities(prev => {
      let next = { ...prev }
      if (checked) {
        items.forEach(item => {
          if ((next[item.id] || 0) === 0) {
            next[item.id] = item.quantity
          }
        })
      } else {
        items.forEach(item => {
          if (!isSessionItem(item)) {
            next[item.id] = 0
          }
        })
      }
      next = updateLinkedRMAQuantities(next)
      return next
    })
  }

  const handleChangeQuantity = (tItem: TableDataItem, quantity: number) => {
    const item = items.find(i => i.id === tItem.id)
    if (!item) {
      return
    }
    setRmaQuantities(prev => {
      let next = { ...prev, [item.id]: quantity }
      if (isSessionItem(item)) {
        next = updateLinkedRMAQuantities(next)
      }
      return next
    })
  }

  useEffect(() => {
    if (initialized.current) {
      return
    }
    initialized.current = true

    setLoading(true)

    const run = async () => {
      const linkedProductIdMap: Record<number, number> = {}
      const params: Record<string, any> = rebill
        ? { latest_cycle: true, status__in: `${ItemStatus.RecycleBillingFailed},${ItemStatus.RecycleFailedHold}` }
        : session?.selected_item
          ? { order: session.selected_item.order.id }
          : {}

      try {
        const queries: Promise<any>[] = [apiClient.get('/order_items/', { params })]
        if (session?.selected_item) {
          queries.push(apiClient.get(`/campaign_products/${session.selected_item.campaign_product.id}/`))
        }
        const results = await Promise.all(queries)
        const orderItemsResult = results[0]
        const campaignProduct = results.length > 1 ? results[1].data : null

        if (campaignProduct?.linked_products) {
          campaignProduct.linked_products.forEach((product: any, index: number) => {
            linkedProductIdMap[product.id] = index
          })
        }

        const resultItems: ActionItem[] = orderItemsResult.data.results || []
        let invalidReturn = false
        let showQtySel = false
        const newItems: ActionItem[] = []
        const newLinked: ActionItem[] = []
        let newQtys: Record<number, number> = {}
        let newTitle = ''
        let newSubtitle = ''
        let newIsOnHold = false

        if (rebill) {
          resultItems.forEach(item => {
            newItems.push(item)
            newQtys[item.id] = item.quantity
          })
          newTitle = 'Confirm Your Subscriptions'
          newSubtitle = 'Please select your subscriptions to remain active'
        } else if (session?.selected_item) {
          newItems.push(session.selected_item)
          newQtys[session.selected_item.id] = 0

          newIsOnHold =
            session.selected_item.fulfillment_status === FulfillmentStatus.Hold ||
            session.selected_item.fulfillment_status === FulfillmentStatus.Frozen ||
            session.selected_item.status === ItemStatus.Pending

          if (!newIsOnHold) {
            if (session.selected_item.fulfillment_status === FulfillmentStatus.None) {
              invalidReturn = true
              resultItems.forEach(item => {
                if (
                  item.fulfillment_status === FulfillmentStatus.Hold ||
                  item.fulfillment_status === FulfillmentStatus.Frozen
                ) {
                  newIsOnHold = true
                  invalidReturn = false
                }
              })
            }
          }

          if (invalidReturn) {
            newQtys[session.selected_item.id] = session.selected_item.quantity
            setItems(newItems)
            setRmaQuantities(newQtys)
            setLoaded(true)
            setLoading(false)
            submitItems(newItems, newQtys, newLinked)
            return
          }

          showQtySel = !newIsOnHold && session.selected_item.quantity > 1
          const isSelectedMainItem = isMainItem(session.selected_item)
          const isSelectedUpsellItem = session.selected_item.campaign_product.product_type === ProductType.Upsale

          resultItems.forEach(item => {
            if (item.id !== session.selected_item.id && item.fulfillment_status !== FulfillmentStatus.NextCycle) {
              if (item.campaign_product.id in linkedProductIdMap) {
                if (newIsOnHold) {
                  if (item.can_cancel) {
                    newLinked.push(item)
                  }
                } else {
                  if (item.can_return || item.can_exchange) {
                    newLinked.push(item)
                  }
                }
              } else if (newIsOnHold) {
                if (item.can_cancel) {
                  const isUpsellItem = item.campaign_product.product_type === ProductType.Upsale
                  const showSetting = session.cancel_items_selection
                  if (
                    showSetting === CancelItemsSelection.ShowAll ||
                    (showSetting === CancelItemsSelection.UpsellsOnly && isUpsellItem) ||
                    (showSetting === CancelItemsSelection.UpsellsOnlyIfUpsell &&
                      (isSelectedMainItem || (isSelectedUpsellItem && isUpsellItem)))
                  ) {
                    newItems.push(item)
                    newQtys[item.id] = 0
                  }
                }
              } else {
                if (item.can_return || item.can_exchange) {
                  const isUpsellItem = item.campaign_product.product_type === ProductType.Upsale
                  const showSetting = session.return_items_selection
                  if (
                    showSetting === CancelItemsSelection.ShowAll ||
                    (showSetting === CancelItemsSelection.UpsellsOnly && isUpsellItem) ||
                    (showSetting === CancelItemsSelection.UpsellsOnlyIfUpsell &&
                      (isSelectedMainItem || (isSelectedUpsellItem && isUpsellItem)))
                  ) {
                    newItems.push(item)
                    newQtys[item.id] = 0
                    if (item.quantity > 1) {
                      showQtySel = true
                    }
                  }
                }
              }
            }
          })

          newTitle = newIsOnHold ? 'Select Items to Cancel' : 'Select Items to Return'
          newSubtitle = newIsOnHold
            ? 'Please select the items from your order you would like to cancel'
            : 'Please select the items from your order you would like to return'

          if (defaults?.items?.length) {
            defaults.items.forEach(d => {
              newQtys[d.id] = d.quantity
            })
          } else {
            newQtys[session.selected_item.id] = session.selected_item.quantity
            for (let i = startIndex; i < selectedItems.length; i++) {
              const item = selectedItems[i]
              if (item.id in newQtys) {
                newQtys[item.id] = item.quantity
              }
            }
          }

          newLinked.sort(
            (a, b) => linkedProductIdMap[a.campaign_product.id] - linkedProductIdMap[b.campaign_product.id]
          )
        }

        setItems(newItems)
        setLinkedItems(newLinked)
        setIsOnHold(newIsOnHold)
        setTitle(newTitle)
        setSubtitle(newSubtitle)
        setShowQuantityColumn(showQtySel)

        if (session?.selected_item) {
          const selectedItemQty = newQtys[session.selected_item.id] || 0
          newLinked.forEach(li => {
            const ratio = li.quantity / session.selected_item.quantity
            newQtys[li.id] = Math.floor(ratio * selectedItemQty + 0.00000001)
          })
          if (newIsOnHold) {
            let hasMain = false
            let allMain = true
            newItems.forEach(item => {
              if (
                item.campaign_product.product_type === ProductType.Offer &&
                item.campaign_product.process_type === ProcessType.Sale
              ) {
                hasMain = true
                if ((newQtys[item.id] || 0) === 0) {
                  allMain = false
                }
              }
            })
            if (hasMain && allMain) {
              newItems.forEach(item => {
                const addon =
                  item.campaign_product.process_type !== ProcessType.Sale ||
                  (item.campaign_product.product_type === ProductType.Upsale &&
                    !!session?.campaign?.auto_cancel_upsells)
                if (addon) {
                  newQtys[item.id] = item.quantity
                }
              })
            }
          }
        }

        setRmaQuantities(newQtys)
        setLoaded(true)
        setLoading(false)

        if (session?.auto_select_single_item && newItems.length === 1 && !showQtySel) {
          const item = newItems[0]
          const finalQtys = { ...newQtys, [item.id]: item.quantity }
          setRmaQuantities(finalQtys)
          submitItems(newItems, finalQtys, newLinked)
        }
      } catch (error) {
        console.error('Error loading action items:', error)
        setLoaded(true)
        setLoading(false)
      }
    }

    run()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Mount-only defaults; re-running would discard the user selection.
  }, [])

  if (isLoading || !loaded) {
    return null
  }

  return (
    <div>
      <StepTitle hr className="text-center" subtitle={subtitle}>
        <div>{title}</div>
      </StepTitle>

      {session && items.length > 0 ? (
        <section className="flex flex-col items-center gap-3 mb-3 w-full select-item-table">
          <div className="w-full mx-auto">
            <div className="w-full px-0 sm:px-3">
              <div className="order-it">
                <table className="table table-responsive order-item-status-table">
                  <thead>
                    <tr>
                      <th className="selectbox">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          ref={input => {
                            if (input) {
                              input.indeterminate = indeterminate
                            }
                          }}
                          onChange={e => handleToggleAll(e.target.checked)}
                          className="check"
                        />
                      </th>
                      <th>Product</th>
                      {showQuantityColumn && <th className="item-quantity">Quantity</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {itemData.map(t => {
                      const full = items.find(i => i.id === t.id) || linkedItems.find(i => i.id === t.id)!
                      return (
                        <tr key={t.id} onClick={() => handleRowClick(t)} className="cursor-pointer">
                          <td className="selectbox">
                            <input
                              type="checkbox"
                              checked={isItemChecked(full)}
                              disabled={!canToggleItem(full)}
                              onChange={e => {
                                e.stopPropagation()
                                handleToggleItem(t, e.target.checked)
                              }}
                              className="check"
                            />
                          </td>
                          <td>
                            <table className="inner-table">
                              <tbody>
                                <tr>
                                  <td>
                                    <img className="product-image order-pic" alt="" src={t.image} />
                                  </td>
                                  <td className="item-title">
                                    <div>
                                      <span className="product-quantity">{t.quantity} × </span>
                                      <span dangerouslySetInnerHTML={{ __html: t.name }} />
                                    </div>
                                    <div className="price">
                                      {t.currentPrice < t.price && (
                                        <span className="line-through text-fg-muted">
                                          {formatCurrency(t.price, t.currency)}
                                        </span>
                                      )}
                                      {formatCurrency(t.currentPrice, t.currency)}
                                    </div>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </td>
                          {showQuantityColumn && (
                            <td className="item-quantity">
                              <select
                                value={rmaQuantities[t.id] || 0}
                                onChange={e => {
                                  e.stopPropagation()
                                  handleChangeQuantity(t, parseInt(e.target.value))
                                }}
                                disabled={!canToggleItem(full)}>
                                <option value="0">0</option>
                                {Array.from({ length: t.quantity }, (_, i) => (
                                  <option key={i + 1} value={i + 1}>
                                    {i + 1}
                                  </option>
                                ))}
                              </select>
                            </td>
                          )}
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>
      ) : (
        <EmptyState title="No items found" description="There are no items available to select right now." />
      )}

      <div className="flex flex-wrap justify-center items-center gap-3 mb-3 w-full">
        {(session?.can_undo || session?.is_step_preview) && (
          <div className="w-full md:w-1/3 lg:w-1/4">
            <button type="button" onClick={() => onBack?.()} className="btn-base back-btn w-full">
              Back
            </button>
          </div>
        )}
        <div className="w-full md:w-1/3 lg:w-1/4">
          <button
            type="button"
            onClick={() => handleDone()}
            className="btn-base input-choice-btn w-full"
            disabled={getSelectionCount() === 0}>
            {label}
          </button>
        </div>
      </div>
    </div>
  )
}

export default SelectActionItems
